import React, { useState } from 'react';

import SectionToggleButton from 'components/SectionToggleButton';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Select from 'react-select';
import { selectLoggedInEmployeeData, selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  EditableTextFieldCSS,
  hideNumberInputArrowsCSS,
  StyledBox,
  StyledGrid,
  StyledTextField,
  StyledTooltip,
  StyledTypography,
} from 'styles/common';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { capitalize, convertDateToString2, getOrgGoalMeasurementTypes, getQueryParams } from 'constants/helper';
import { GOAL_EVENTS1, sendGoalEvent } from 'constants/mixpanelHelper';

import { HeaderText, IndianNumberFormatCustom, ForeignNumberFormatCustom } from '../Goals/GoalHelperComponents';
import CollaboratorField from './CollaboratorField';
import DescriptionSection from './DescriptionSection';
import DueDateField from './DueDateField';
import TrackingField from './GoalTracking/TrackingField';
import ObjectiveField from './ObjectiveField';
import OwnerField from './OwnerField';
import VisibilityField from './VisibilityField';

// need to move common functions in one file. using below one with export
export function Heading({ children, ...props }) {
  return (
    <StyledTypography variant="body2" weight={600} color="textSecondary" {...props}>
      {children}
    </StyledTypography>
  );
}

const DATE_REASONS = {
  DELAYED: 'DELAYED',
  ONGOING: 'ONGOING',
  COMPLETED_ON_TIME: 'COMPLETED_ON_TIME',
  COMPLETED_WITH_DELAY: 'COMPLETED_WITH_DELAY',
};

export const getDateTooltip = ({ date, status, reason }) => {
  const readableDate = date ? convertDateToString2(date) : null;

  // In case of tasks, due date or start date can be null.
  // Returning '' will make sure to hide the blank tooltip.
  if (!readableDate) return '';

  switch (status) {
    case DATE_REASONS.DELAYED:
      return (
        <StyledBox flexcontainer justifycontent="center" alignitems="center" flexdirection="column">
          <b>{reason}</b>
          Due date was {readableDate}
        </StyledBox>
      );
    case DATE_REASONS.ONGOING:
      return (
        <StyledBox flexcontainer justifycontent="center" alignitems="center" flexdirection="column">
          <b>{reason}</b>
          <br />
          Due date is {readableDate}
        </StyledBox>
      );
    case DATE_REASONS.COMPLETED_ON_TIME:
      return (
        <StyledBox flexcontainer justifycontent="center" alignitems="center" flexdirection="column">
          <b>{reason}</b>
          {readableDate}
        </StyledBox>
      );
    case DATE_REASONS.COMPLETED_WITH_DELAY:
      return (
        <StyledBox flexcontainer justifycontent="center" alignitems="center" flexdirection="column">
          <b>{reason}</b>
          {readableDate}
        </StyledBox>
      );
    default:
      return (
        <StyledBox flexcontainer justifycontent="center" alignitems="center" flexdirection="column">
          <b>{reason}</b>
          {readableDate}
        </StyledBox>
      );
  }
};

function MeasureComponent({ goalDetails, updateGoal }) {
  const [isHovered, setIsHovered] = useState(false);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const orgGoalMeasurementTypes = getOrgGoalMeasurementTypes(loggedInEmployee?.organization?.currency ?? null);
  const optionsForSelect = orgGoalMeasurementTypes.map(({ symbol, code }) => ({
    label: `${capitalize(code)} (${symbol})`,
    value: code,
  }));

  const handleMouseEnter = (e) => {
    setIsHovered(true);
  };

  const handleMouseLeave = (e) => {
    setIsHovered(false);
  };

  return (
    <DropdownWrap onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <MeasuredWithSelect
        menuShouldScrollIntoView={false}
        options={optionsForSelect}
        value={optionsForSelect.filter(({ value }) => value === goalDetails.measure)[0]}
        onChange={({ value }) => {
          updateGoal({ ...goalDetails, measure: value }, false);
        }}
        classNamePrefix="measured-with"
        styles={customStyles}
        isHovered={isHovered}
      />
    </DropdownWrap>
  );
}

export function RestrictedAccessContainerWithTooltip({ disabled, reason, children }) {
  if (!reason || !disabled) {
    return <RestrictedAccessContainer disabled={disabled}>{children}</RestrictedAccessContainer>;
  }

  return (
    <StyledTooltip title={reason ?? null}>
      <RestrictedAccessContainer disabled={disabled}>{children}</RestrictedAccessContainer>
    </StyledTooltip>
  );
}

export default function DetailSection({
  taskDetails,
  updateTask,
  toggleShowTrackingDetails,
  getTaskData,
  disabled = false,
  reason = '',
  pageName = { pageName }, // used in tracking events
}) {
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const { empId } = queryParams;
  const [areMoreDetailsVisible, setMoreDetailsVisibility] = useState(false);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);

  const getPrimaryEmployeeWeightageDetails = () => {
    if (empId) {
      const primaryEmployee = taskDetails?.toIds.filter((emp) => emp.id === empId)[0];
      if (primaryEmployee) {
        return primaryEmployee;
      }
      return taskDetails.toIds[0];
    }
    const loggedInUserExistsInToIds = taskDetails.toIds.filter((emp) => emp.id === loggedInEmployeeId);
    if (loggedInUserExistsInToIds?.length > 0) {
      return loggedInUserExistsInToIds[0];
    }
    return taskDetails?.toIds[0];
  };

  const employeeObj = useSelector(selectLoggedInEmployeeData);
  let orgCurrency = employeeObj?.organization?.currency ?? null;
  if (!orgCurrency) {
    orgCurrency = 'INR';
  }

  const coloredDueDate = taskDetails.coloredDueDate || {};
  const coloredStartDate = taskDetails.coloredStartDate || {};

  function onBlur(e) {
    const { value, name, type } = e.target;
    let parsedValue = type === 'number' ? Number(value) : value;
    const defaultValue = type === 'number' ? 0 : '';
    const oldValue = taskDetails[name] || defaultValue;

    if (name === 'startingProgressValue' || name === 'target') parsedValue = parsedValue.replace(/,/g, '');

    const hasValueChanged = oldValue !== parsedValue;
    if (hasValueChanged) {
      sendGoalEvent({
        page: { pageName, flow: 'Activity' },
        eventName: GOAL_EVENTS1.updated,
        field: name,
        referrer: 'SidePanel',
      });
      updateTask({ ...taskDetails, [name]: parsedValue }, false);
    }
  }

  function onDescriptionEnter(e) {
    e.preventDefault();
    e.target.blur();
  }

  function onDescriptionEscape(e) {
    e.stopPropagation();
    e.preventDefault();
    e.target.value = taskDetails.description;
    e.target.blur();
  }

  function onKeyDown(e) {
    if (e.keyCode === 13) {
      onDescriptionEnter(e);
    } else if (e.keyCode === 27) {
      onDescriptionEscape(e);
    }
  }

  function renderObjectiveSection() {
    return (
      <StyledGrid bottomGutter={2} container>
        <StyledGrid alignSelf="center" item xs={3}>
          <Heading topGutter={1}>Objective</Heading>
        </StyledGrid>
        <StyledGrid item xs={9}>
          <ObjectiveField
            taskDetails={taskDetails}
            updateTask={(...params) => {
              sendGoalEvent({
                page: { pageName, flow: 'Activity' },
                eventName: GOAL_EVENTS1.updated,
                field: 'objective',
                referrer: 'SidePanel',
                actionType: GOAL_EVENTS1.UpdatedObjective,
              });
              updateTask(...params);
            }}
          />
        </StyledGrid>
      </StyledGrid>
    );
  }

  function renderVisibilitySection() {
    return (
      <StyledGrid bottomGutter={2} container>
        <StyledGrid alignSelf="center" item xs={3}>
          <Heading topGutter={1}>Visibility</Heading>
        </StyledGrid>
        <StyledGrid item xs={9}>
          <VisibilityField taskDetails={taskDetails} updateTask={updateTask} />
        </StyledGrid>
      </StyledGrid>
    );
  }

  function renderTrackingSection() {
    return (
      <StyledGrid bottomGutter={2} container>
        <StyledGrid alignSelf="center" item xs={3}>
          <Heading topGutter={1}>Tracking</Heading>
        </StyledGrid>
        <StyledGrid item xs={9}>
          <TrackingField
            taskDetails={taskDetails}
            updateTask={updateTask}
            toggleShowTrackingDetails={toggleShowTrackingDetails}
            getTaskData={getTaskData}
          />
        </StyledGrid>
      </StyledGrid>
    );
  }

  const updatedWeightageToolTipTitle = `Prioritize your ${modifyString(
    moduleName,
    StringModifier.PLURAL_SENTENCE_CASE
  )} by assigning different weights to them.`;
  return (
    <Container>
      <RestrictedAccessContainerWithTooltip disabled={disabled} reason={reason}>
        <HeaderText weight={600}>Details</HeaderText>
        <DescriptionSection taskDetails={taskDetails} updateTask={updateTask} />
        {/* {renderObjectiveSection()} */}
        {renderVisibilitySection()}
        {renderTrackingSection()}
      </RestrictedAccessContainerWithTooltip>
      {/* Objective and Owner Headings + components */}
      <StyledGrid container>
        <StyledGrid item xs={6}>
          <Heading>Owner(s)</Heading>
        </StyledGrid>
        <StyledGrid item xs={6}>
          <Heading>Collaborator(s)</Heading>
        </StyledGrid>
      </StyledGrid>
      <StyledGrid bottomGutter={2} container>
        <StyledGrid item xs={6}>
          <StyledOwnerField
            taskDetails={taskDetails}
            updateTask={(...params) => {
              sendGoalEvent({
                page: { pageName, flow: 'Activity' },
                eventName: GOAL_EVENTS1.UpdatedOwnerField,
                field: 'owners',
                referrer: 'SidePanel',
              });
              updateTask(...params);
            }}
          />
        </StyledGrid>
        <StyledGrid item xs={6}>
          <StyledCollaboratorField
            taskDetails={taskDetails}
            updateTask={(...params) => {
              sendGoalEvent({
                page: { pageName, flow: 'Activity' },
                eventName: GOAL_EVENTS1.UpdatedCollaboratorsField,
                field: 'collaborators',
                referrer: 'SidePanel',
              });
              updateTask(...params);
            }}
          />
        </StyledGrid>
      </StyledGrid>

      {areMoreDetailsVisible && (
        <>
          {/* Start Value and Target Value Headings + components */}
          <RestrictedAccessContainerWithTooltip disabled={disabled} reason={reason}>
            <StyledGrid container>
              <StyledGrid item xs={6}>
                <Heading>Start Value</Heading>
              </StyledGrid>
              <StyledGrid item xs={6}>
                <Heading>Target Value</Heading>
              </StyledGrid>
            </StyledGrid>
            <StyledGrid bottomGutter={2} container>
              <StyledGrid item xs={6}>
                <StartingField
                  name="startingProgressValue"
                  defaultValue={taskDetails.startingProgressValue || '-'}
                  onBlur={onBlur}
                  variant="outlined"
                  size="small"
                  onKeyDown={onKeyDown}
                  InputProps={{
                    inputComponent: orgCurrency === 'INR' ? IndianNumberFormatCustom : ForeignNumberFormatCustom,
                  }}
                />
              </StyledGrid>
              <StyledGrid item xs={6}>
                {
                  // dont give type === number, as it is being handled by NumberFormatCustom
                }
                <StartingField
                  name="target"
                  defaultValue={taskDetails.target || '-'}
                  onBlur={onBlur}
                  variant="outlined"
                  size="small"
                  onKeyDown={onKeyDown}
                  InputProps={{
                    inputComponent: orgCurrency === 'INR' ? IndianNumberFormatCustom : ForeignNumberFormatCustom,
                  }}
                />
              </StyledGrid>
            </StyledGrid>
          </RestrictedAccessContainerWithTooltip>
          {/* Weightage and Due date Headings + components */}
          <StyledGrid container>
            <StyledGrid item xs={6}>
              <Heading>Metric Name</Heading>
            </StyledGrid>
            <StyledGrid item xs={6}>
              <Heading>Measure</Heading>
            </StyledGrid>
          </StyledGrid>
          <StyledGrid bottomGutter={2} container>
            <>
              <StyledGrid item xs={6}>
                <RestrictedAccessContainerWithTooltip disabled={disabled} reason={reason}>
                  <StartingField
                    name="measureType"
                    defaultValue={taskDetails.measureType ?? '-'}
                    onBlur={onBlur}
                    variant="outlined"
                    size="small"
                    onKeyDown={onKeyDown}
                    placeholder="e.g. Revenue, NPS etc."
                  />
                </RestrictedAccessContainerWithTooltip>
              </StyledGrid>
            </>
            <StyledGrid item xs={6}>
              <RestrictedAccessContainerWithTooltip disabled={disabled} reason={reason}>
                <div style={{ width: '50%' }}>
                  <MeasureComponent
                    goalDetails={taskDetails}
                    updateGoal={(...params) => {
                      sendGoalEvent({
                        page: { pageName, flow: 'Activity' },
                        eventName: GOAL_EVENTS1.updated,
                        field: 'measure',
                        referrer: 'SidePanel',
                      });
                      updateTask(...params);
                    }}
                  />
                </div>
              </RestrictedAccessContainerWithTooltip>
            </StyledGrid>
          </StyledGrid>
          {/* <StyledGrid bottomGutter={2} container>
            <StyledGrid item xs={6}>
              <VerticallyAlignedFlexContainer>
                <Heading>
                  {loggedInEmployeeId === getPrimaryEmployeeWeightageDetails()?.id
                    ? 'My'
                    : getPrimaryEmployeeWeightageDetails()?.displayName + "'s"}{' '}
                  Weightage
                </Heading>
                <TooltipInfo
                  title={updatedWeightageToolTipTitle}
                  linkUrl={KH_LINKS.GOAL_WEIGHTAGE.url}
                  leftGutter={1}
                />
              </VerticallyAlignedFlexContainer>
              <GoalsWeightProvider>
                <GoalWeightageSection
                  taskDetails={taskDetails}
                  primaryEmployee={getPrimaryEmployeeWeightageDetails()}
                />
                <GoalsWeightSidepanel />
              </GoalsWeightProvider>
            </StyledGrid>
            <StyledGrid item xs={6}>
            </StyledGrid>
          </StyledGrid> */}
          <RestrictedAccessContainerWithTooltip disabled={disabled} reason={reason}>
            <StyledGrid container>
              <StyledGrid item xs={6}>
                <Heading>Start On</Heading>
              </StyledGrid>
              <StyledGrid item xs={6}>
                <Heading>Due Date</Heading>
              </StyledGrid>
            </StyledGrid>
            <StyledGrid bottomGutter={1} container>
              <StyledGrid item xs={6}>
                <DueDateField
                  clearable={false}
                  dueDate={coloredStartDate.timeStamp ? new Date(coloredStartDate.timeStamp) : null}
                  color={coloredStartDate.color}
                  tooltipText={getDateTooltip({
                    date: coloredStartDate.timeStamp,
                    reason: coloredStartDate.reason,
                  })}
                  isCrossed={coloredStartDate.isCrossed}
                  detailedStatus={taskDetails.detailedStatus}
                  updateDueDate={(date) => {
                    sendGoalEvent({
                      page: { pageName, flow: 'Activity' },
                      eventName: GOAL_EVENTS1.UpdatedStartDate,
                      field: 'startDate',
                      referrer: 'SidePanel',
                    });
                    updateTask(
                      {
                        ...taskDetails,
                        startDate: date,
                        coloredStartDate: {
                          ...coloredStartDate,
                          timeStamp: date ? date.getTime() : null,
                        },
                      },
                      false
                    );
                  }}
                />
              </StyledGrid>
              <StyledGrid item xs={6}>
                <DueDateField
                  clearable={false}
                  dueDate={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
                  color={coloredDueDate.color}
                  tooltipText={getDateTooltip({
                    date: coloredDueDate.timeStamp,
                    status: taskDetails.detailedStatus,
                    reason: coloredDueDate.reason,
                  })}
                  isCrossed={coloredDueDate.isCrossed}
                  detailedStatus={taskDetails.detailedStatus}
                  updateDueDate={(date) => {
                    sendGoalEvent({
                      page: { pageName, flow: 'Activity' },
                      eventName: GOAL_EVENTS1.UpdatedEndDate,
                      field: 'dueDate',
                      referrer: 'SidePanel',
                    });
                    updateTask(
                      {
                        ...taskDetails,
                        dueDate: date,
                        coloredDueDate: {
                          ...coloredDueDate,
                          timeStamp: date ? date.getTime() : null,
                        },
                      },
                      false
                    );
                  }}
                />
              </StyledGrid>
            </StyledGrid>
          </RestrictedAccessContainerWithTooltip>
        </>
      )}
      <SectionToggleButton
        topGutter={areMoreDetailsVisible ? 2 : -1}
        bottomGutter={-1}
        isSectionVisible={areMoreDetailsVisible}
        toggleSectionVisibility={setMoreDetailsVisibility}
      >
        {areMoreDetailsVisible ? 'Show less details' : 'Show more details'}
      </SectionToggleButton>
    </Container>
  );
}

const Container = styled.div`
  margin-bottom: 2rem;
`;

const InputPaddingCSS = css`
  padding: 0.5rem 0.75rem;
`;

const StartingField = styled(StyledTextField)`
  .MuiInputBase-root {
    font-size: ${(props) => props.theme.typography.body2.fontSize};
    height: 2.5rem;
  }
  ${EditableTextFieldCSS};
  input {
    ${hideNumberInputArrowsCSS};
    ${InputPaddingCSS};
  }
  width: 50%;
  // margin-left: -0.5rem;
`;

// const StyledNodesView = styled(NodesView)`
//   ${InputPaddingCSS};
//   &:hover {
//     border: 1px solid ${props => props.theme.palette.borders.lightGrey};
//   }
//   margin-left: -0.5rem;
//   width: 80%;
// `;

// const StyledEmptyNodeView = styled(EmptyNodeView)`
//   position: relative;
// `;

const StyledOwnerField = styled(OwnerField)`
  margin-left: 0.5rem;
`;

const StyledCollaboratorField = styled(CollaboratorField)`
  margin-left: 0.5rem;
`;

const RestrictedAccessContainer = styled.div`
  ${(props) =>
    props.disabled &&
    css`
      > * {
        cursor: initial;
        pointer-events: none;
      }
    `}
`;

const MeasuredWithSelect = styled(Select)`
  .measured-with__control {
    height: 2.5rem;
  }
`;

const customStyles = {
  control: (base, { selectProps: { isHovered } }) => ({
    ...base,
    width: '100%',
    border: isHovered ? '1px solid hsl(0, 0%, 80%)' : '1px solid transparent',
    cursor: 'pointer',
  }),
  indicatorsContainer: (base, { selectProps: { isHovered } }) => ({
    ...base,
    visibility: isHovered ? 'visible' : 'hidden',
  }),
  menu: (provided, state) => ({
    ...provided,
    marginTop: 0,
    marginBottom: 0,
  }),
};

const DropdownWrap = styled.div``;
